/**
 * Simple i18n translation system for map-lib
 */

export type SupportedLanguage = 'en' | 'fr';

const translations = {
  en: {
    'legend.tooltip.clickToHide': 'Click to hide',
    'legend.tooltip.clickToShow': 'Click to show',
  },
  fr: {
    'legend.tooltip.clickToHide': 'Cliquer pour masquer',
    'legend.tooltip.clickToShow': 'Cliquer pour afficher',
  },
};

export class I18n {
  private static instance: I18n;
  private currentLanguage: SupportedLanguage;

  private constructor(language?: SupportedLanguage) {
    this.currentLanguage = language || this.detectBrowserLanguage();
  }

  /**
   * Get or create the i18n instance
   * @param language - Optional language to set
   */
  public static getInstance(language?: SupportedLanguage): I18n {
    if (!I18n.instance) {
      I18n.instance = new I18n(language);
    } else if (language && language !== I18n.instance.currentLanguage) {
      I18n.instance.setLanguage(language);
    }
    return I18n.instance;
  }

  /**
   * Detect browser language and return supported language
   */
  private detectBrowserLanguage(): SupportedLanguage {
    if (typeof navigator === 'undefined') {
      return 'en'; // Default for server-side
    }

    const browserLang = navigator.language.toLowerCase();

    // Check if browser language starts with 'fr'
    if (browserLang.startsWith('fr')) {
      return 'fr';
    }

    // Default to English
    return 'en';
  }

  /**
   * Set the current language
   */
  public setLanguage(language: SupportedLanguage): void {
    this.currentLanguage = language;
  }

  /**
   * Get the current language
   */
  public getLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  /**
   * Translate a key
   * @param key - Translation key (e.g., 'legend.tooltip.clickToHide')
   * @returns Translated string
   */
  public t(key: keyof typeof translations.en): string {
    const translation = translations[this.currentLanguage][key];

    if (!translation) {
      console.warn(`Translation missing for key: ${key} in language: ${this.currentLanguage}`);
      return key;
    }

    return translation;
  }
}
